import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Plus, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { SearchInput } from "@/components/SearchInput";
import { VehicleForm } from "@/components/vehicles/VehicleForm";
import {
  VehicleDeadlineBadges,
  VehicleStatusBadge,
} from "@/components/vehicles/VehicleBadges";
import {
  useCreateVehicle,
  useVehicles,
  vehicleTypeLabels,
  type Vehicle,
} from "@/hooks/useVehicles";
import { matchesSearchTokens } from "@/lib/search-utils";
import { cn } from "@/lib/utils";

function VehicleLinkCell({
  vehicle,
  className,
  children,
}: {
  vehicle: Vehicle;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <TableCell className={cn("p-0", className)}>
      <Link
        to={`/vehicles/${vehicle.id}`}
        className="block h-full p-4 text-inherit no-underline"
      >
        {children}
      </Link>
    </TableCell>
  );
}

const VehiclesPage = () => {
  const navigate = useNavigate();
  const { data: vehicles, isLoading } = useVehicles();
  const createVehicle = useCreateVehicle();
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const filtered = useMemo(
    () =>
      vehicles.filter((vehicle) =>
        matchesSearchTokens(
          [
            vehicle.name,
            vehicle.brand,
            vehicle.model,
            vehicle.licensePlate,
            vehicleTypeLabels[vehicle.vehicleType],
          ]
            .filter(Boolean)
            .join(" "),
          search,
        ),
      ),
    [vehicles, search],
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Vehicles">
        <Button
          offlineEdit
          onClick={() => setIsCreateOpen(true)}
          data-testid="new-vehicle-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Vehicle
        </Button>
      </PageHeader>

      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder="Search by name, brand, model, license plate..."
      />

      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-px" />
              <TableHead>Name</TableHead>
              <TableHead className="whitespace-nowrap">License plate</TableHead>
              <TableHead>Brand / model</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Attention</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center">
                  Loading vehicles...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <Truck className="h-8 w-8" />
                    <p>
                      {vehicles.length === 0
                        ? "No vehicles yet"
                        : "No vehicles match your search"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((vehicle) => {
                const photo = vehicle.images?.[0]?.imageURL;
                const openDamages = vehicle.damages?.length ?? 0;
                return (
                  <TableRow
                    key={vehicle.id}
                    className="cursor-pointer hover:bg-muted/50"
                    data-testid={`vehicle-row-${vehicle.id}`}
                  >
                    <VehicleLinkCell vehicle={vehicle} className="w-px">
                      {photo ? (
                        <img
                          src={photo}
                          alt=""
                          loading="lazy"
                          className="h-10 w-10 max-w-none rounded-md border object-cover sm:h-12 sm:w-12"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md border bg-muted sm:h-12 sm:w-12">
                          <Truck className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </VehicleLinkCell>
                    <VehicleLinkCell
                      vehicle={vehicle}
                      className="whitespace-nowrap font-medium"
                    >
                      {vehicle.name}
                    </VehicleLinkCell>
                    <VehicleLinkCell
                      vehicle={vehicle}
                      className="whitespace-nowrap font-mono text-sm"
                    >
                      {vehicle.licensePlate || "-"}
                    </VehicleLinkCell>
                    <VehicleLinkCell vehicle={vehicle}>
                      {[vehicle.brand, vehicle.model]
                        .filter(Boolean)
                        .join(" ") || "-"}
                    </VehicleLinkCell>
                    <VehicleLinkCell vehicle={vehicle}>
                      {vehicleTypeLabels[vehicle.vehicleType]}
                    </VehicleLinkCell>
                    <VehicleLinkCell vehicle={vehicle}>
                      <VehicleStatusBadge status={vehicle.status} />
                    </VehicleLinkCell>
                    <VehicleLinkCell vehicle={vehicle}>
                      <div className="flex flex-wrap gap-1">
                        {openDamages > 0 && (
                          <Badge
                            variant="outline"
                            className="gap-1 border-amber-500 text-amber-600 dark:text-amber-400"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {openDamages} open{" "}
                            {openDamages === 1 ? "damage" : "damages"}
                          </Badge>
                        )}
                        <VehicleDeadlineBadges vehicle={vehicle} />
                      </div>
                    </VehicleLinkCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>New Vehicle</DialogTitle>
          </DialogHeader>
          <VehicleForm
            compact
            submitLabel="Create Vehicle"
            isPending={createVehicle.isPending}
            onCancel={() => setIsCreateOpen(false)}
            onSubmit={(input) =>
              createVehicle.mutate(input, {
                onSuccess: ({ id }) => {
                  setIsCreateOpen(false);
                  navigate(`/vehicles/${id}`);
                },
              })
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehiclesPage;
